import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees } from '@/api/generated/company-management-user/company-management-user'
import type { EmployeeUserResponse, ServiceModuleModel } from '@/api/model'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import { Button } from '@/components/shadcn/button'
import { FilterRow } from '@/components/common/filters/FilterRow'
import { FilterDateRangePicker } from '@/components/common/filters/FilterDateRangePicker'
import { useTravelExpenseEntries } from './hooks/useTravelExpenseEntries'
import { TravelExpenseExportButton } from './components/TravelExpenseExportButton'
import { TravelExpenseEntriesTable } from './components/TravelExpenseEntriesTable'
import { monthToDateRange } from './travelExpenseFormat'
import { unwrap } from '@/api/unwrap'

interface Props {
  module: ServiceModuleModel
  /** Fixed employee of this view (from the URL, `?view=employee&employeeId=…`). */
  employeeId: string
  /** Month selected in the overview (`YYYY-MM`), preselects the date range. */
  month: string
  onBack: () => void
}

/** Travel days of one employee (row click in the monthly overview → here). */
export function TravelExpenseEmployeeEntriesPage({ module, employeeId, month, onBack }: Props) {
  const { t } = useTranslation(['service-modules-travel-expenses', 'common', 'filters'])
  const [range, setRange] = useState<{ from?: string; to?: string }>(() => monthToDateRange(month))

  const employeesQuery = useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees(module.id)
  const employee = useMemo(
    () => (unwrap<EmployeeUserResponse[]>(employeesQuery.data) ?? []).find((e) => e.id === employeeId),
    [employeesQuery.data, employeeId],
  )

  const entries = useTravelExpenseEntries(module.id, { employeeUserId: employeeId, from: range.from, to: range.to })

  const name =
    `${employee?.firstName ?? ''} ${employee?.lastName ?? ''}`.trim() || t('dropdown.employeeFallback', { ns: 'filters' })

  return (
    <PageLayout>
      <PageHeader title={t('employeeTitle', { name })}>
        <PageHeaderActions>
          <TravelExpenseExportButton
            moduleId={module.id}
            filters={{ employeeUserId: employeeId, from: range.from, to: range.to }}
          />
          <Button variant="outline" className="h-9 gap-2" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            {t('buttons.back', { ns: 'common' })}
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <FilterRow>
        <FilterDateRangePicker from={range.from} to={range.to} onChange={setRange} />
      </FilterRow>

      <TravelExpenseEntriesTable entries={entries} showEmployee={false} />
    </PageLayout>
  )
}
